import React, { useState, useEffect } from 'react';
import FestivalGreeting from '../../components/festival/FestivalGreeting';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle, Clock, AlertTriangle, TrendingUp, MessageSquare } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; } catch { return '-'; } };
const today = () => new Date().toISOString().split('T')[0];

export default function WorkerDashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [openQueries, setOpenQueries] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tasks/my').then(r => setTasks(r.data)).finally(() => setLoading(false));
    api.get('/queries', { params: { my: true, status: 'open' } }).then(r => setOpenQueries(r.data.length)).catch(()=>{});
  }, []);

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    delayed: tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date < today()).length,
  };

  const urgentTasks = tasks.filter(t => t.status !== 'completed' && (t.priority === 'urgent' || t.priority === 'high')).slice(0, 5);
  const upcomingTasks = tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date >= today()).sort((a, b) => a.due_date > b.due_date ? 1 : -1).slice(0, 5);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-5 sm:space-y-6 max-w-4xl mx-auto">
      <FestivalGreeting />
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Welcome, {user?.name}! 👋</h1>
        <p className="text-gray-500 text-xs sm:text-sm">Here's your work summary</p>
        {user?.departments?.length > 0 && (
          <div className="flex gap-1 sm:gap-2 mt-2 flex-wrap">
            {user.departments.map(d => <span key={d.id} className="text-xs bg-blue-50 text-blue-700 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border border-blue-100">{d.name}</span>)}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        {[
          { label: 'Total Tasks', value: stats.total, icon: TrendingUp, color: 'text-gray-700', bg: 'bg-gray-100' },
          { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-100' },
          { label: 'In Progress', value: stats.inProgress, icon: Clock, color: 'text-blue-700', bg: 'bg-blue-100' },
          { label: 'Delayed', value: stats.delayed, icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-100' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center`}>
            <s.icon className={`w-5 sm:w-6 h-5 sm:h-6 ${s.color} mx-auto mb-1 sm:mb-2`} />
            <p className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className={`text-xs font-medium ${s.color} opacity-80`}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Open Queries Alert */}
      {openQueries > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg sm:rounded-xl flex-shrink-0"><MessageSquare className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" /></div>
            <div className="min-w-0">
              <p className="font-semibold text-orange-800 text-sm">{openQueries} Query Pending</p>
              <p className="text-xs text-orange-600">Admin has not responded yet</p>
            </div>
          </div>
          <a href="/worker/queries" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-2 rounded-xl whitespace-nowrap text-center">View →</a>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5">
        {/* Urgent Tasks */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-red-50">
            <h2 className="font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Urgent / High Priority</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {urgentTasks.length === 0 ? <p className="p-5 text-gray-400 text-sm text-center">No urgent tasks 🎉</p> : urgentTasks.map(t => (
              <div key={t.id} className="p-4">
                <p className="font-medium text-gray-900 text-sm">{t.task_title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.project_name} ({t.proj_code})</p>
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium priority-${t.priority}`}>{t.priority}</span>
                  {t.due_date && <span className="text-xs text-gray-500">Due: {safeDate(t.due_date)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-blue-50">
            <h2 className="font-semibold text-blue-800 flex items-center gap-2"><Clock className="w-4 h-4" /> Upcoming Deadlines</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {upcomingTasks.length === 0 ? <p className="p-5 text-gray-400 text-sm text-center">No upcoming tasks</p> : upcomingTasks.map(t => (
              <div key={t.id} className="p-4">
                <p className="font-medium text-gray-900 text-sm">{t.task_title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.project_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${t.status}`}>{t.status}</span>
                  <span className="text-xs text-blue-600 font-medium">{safeDate(t.due_date)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
